// "Is this browser already signed in?" — asked of Authentik DIRECTLY, via
// its users/me endpoint through the same-origin /idp Caddy proxy, rather
// than by poking the login flow (the previous checkExistingSession()).
//
// Why: the flow-based probe treated exactly one first response
// (xak-flow-redirect + final_redirect) as "logged in" and EVERYTHING else —
// an intermediate redirect, a stale half-finished flow plan left in the
// Authentik session by an earlier failed attempt, an identification stage,
// or any fetch/network error — as "anonymous", i.e. the login form. That
// was the intermittent "refresh drops me to the login screen" bug. It also
// round-tripped through Authentik's authorize endpoint on every refresh,
// minting a fresh auth code each time for no reason.
//
// users/me is stateless and unambiguous (verified against the live proxy):
//   200 + user JSON  -> a valid authentik_session cookie was sent
//   401 / 403        -> no (or expired) session: "Authentication credentials
//                       were not provided."
//   anything else    -> we could NOT determine it (network error, 5xx,
//                       non-JSON body such as an HTML error page) — which is
//                       a different answer from "not logged in" and must
//                       never be rendered as the login form.

export type SessionProbe = 'authenticated' | 'anonymous' | 'unavailable';

export const SESSION_PROBE_URL = '/idp/api/v3/core/users/me/';

export async function probeSession(): Promise<SessionProbe> {
  let res: Response;
  try {
    res = await fetch(SESSION_PROBE_URL, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
  } catch {
    return 'unavailable'; // network error, DNS, proxy down, CORS…
  }

  if (res.status === 401 || res.status === 403) return 'anonymous';

  if (res.status === 200) {
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object') return 'authenticated';
    } catch {
      // fall through — a 200 that isn't JSON is not Authentik answering us
    }
    return 'unavailable';
  }

  // 5xx, 502 from the proxy, unexpected 3xx/4xx — can't tell
  return 'unavailable';
}
